import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Search, 
  Calendar, 
  Clock, 
  User, 
  Tag, 
  ChevronRight, 
  Sparkles, 
  ShieldCheck, 
  Zap, 
  Mail,
  Globe,
  MessageSquare,
  Share2
} from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import { marketingNavLinks } from "../constants/marketingNav";

const categories = [
  { id: "all", label: "All Posts" },
  { id: "product", label: "Product News" },
  { id: "engineering", label: "Engineering" },
  { id: "security", label: "Security" },
  { id: "ai", label: "AI & Future" }
];

const blogPosts = [
  {
    id: 1,
    title: "How Gemini AI is Revolutionizing Task Extraction",
    excerpt: "Discover how we integrated large language models to transform complex project briefs into actionable Kanban boards instantly.",
    author: "Amzad",
    date: "Feb 12, 2012",
    readTime: "6 min read",
    category: "ai",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800",
    tags: ["AI", "Gemini", "Productivity"]
  },
  {
    id: 2,
    title: "Securing Your Project Secrets: The Vault Philosophy",
    excerpt: "Why we built a zero-knowledge credentials manager directly into PMS and how it protects your enterprise data.",
    author: "Aman",
    date: "Feb 05, 2026",
    readTime: "8 min read",
    category: "security",
    image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=800",
    tags: ["Security", "Encryption", "Enterprise"]
  },
  {
    id: 3,
    title: "Optimizing Salesforce Workflows for Engineering Teams",
    excerpt: "A deep dive into bridging the gap between sales opportunities and technical delivery using our new two-way sync.",
    author: "Saan",
    date: "Jan 28, 2026",
    readTime: "5 min read",
    category: "engineering",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800",
    tags: ["Salesforce", "Integration", "Workflow"]
  },
  {
    id: 4,
    title: "Remote-First Leadership: Building Culture in Noida & Beyond",
    excerpt: "How Leanport Software manages global teams and maintains high-velocity delivery across multiple time zones.",
    author: "Tazim Haque",
    date: "Jan 15, 2026",
    readTime: "10 min read",
    category: "product",
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=800",
    tags: ["Culture", "Remote", "Leadership"]
  }
];

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredPosts = useMemo(() => {
    return blogPosts.filter(post => {
      const matchesCategory = activeCategory === "all" || post.category === activeCategory;
      const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                           post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "Integrations") return { ...link, route: "/integrations" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Changelog") return { ...link, route: "/changelog" };
        if (link.label === "Security") return { ...link, route: "/security" };
        if (link.label === "Blog") return { ...link, route: "/blog" };
        if (link.label === "About Us") return { ...link, route: "/about-us" };
        if (link.label === "Careers") return { ...link, route: "/careers" };
        if (link.label === "FAQ") return { ...link, route: "/faq" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  const smoothScroll = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = 96;
    const offset = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offset, behavior: "smooth" });
  }, []);

  const handleNavClick = useCallback(
    (e, target) => {
      e.preventDefault();
      smoothScroll(target);
    },
    [smoothScroll]
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .section-animate, .landing-page .reveal, .landing-page .reveal-left, .landing-page .reveal-right"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white">
      <MarketingHeader navLinks={navLinks} onNavClick={handleNavClick} ctaTarget="contact" />

      <main>
        {/* Hero Section */}
        <section id="hero" className="curved-section shell-sky section-animate relative min-h-[60vh] flex items-center overflow-hidden pt-20">
          <div className="absolute inset-0 opacity-40">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-400 rounded-full blur-[120px] -mr-40 -mt-40" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500 rounded-full blur-[100px] -ml-20 -mb-20" />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <div className="reveal">
              <span className="section-eyebrow">Insights & Perspectives</span>
              <h1 className="text-navy mt-6 mb-8">
                The <span className="text-blue-600">PMS</span> Blog.
              </h1>
              <p className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed">
                Expert takes on AI, engineering culture, and the future of secure project management.
              </p>
              
              <div className="max-w-2xl mx-auto relative group reveal stagger-1">
                <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
                <input
                  type="text"
                  placeholder="Search articles, topics, authors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-16 pr-6 py-5 bg-white border border-gray-100 rounded-[32px] shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all text-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="shell-white py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-2 mb-16 reveal">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-8 py-3 rounded-full font-bold transition-all text-sm uppercase tracking-widest ${
                    activeCategory === cat.id
                      ? "bg-navy text-sky shadow-xl shadow-navy/20"
                      : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Featured Post (only on 'all' and if exists) */}
            {activeCategory === 'all' && filteredPosts.length > 0 && searchQuery === "" && (
              <div className="mb-20 reveal stagger-1">
                <div className="group relative bg-white rounded-[48px] overflow-hidden border border-gray-100 shadow-2xl transition-all hover:border-blue-200">
                  <div className="grid lg:grid-cols-2">
                    <div className="h-80 lg:h-full overflow-hidden">
                      <img 
                        src={filteredPosts[0].image} 
                        alt={filteredPosts[0].title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-12 lg:p-16 flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-6">
                        <span className="px-3 py-1 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded">Featured Post</span>
                        <span className="text-sm font-bold text-gray-400">{filteredPosts[0].date}</span>
                      </div>
                      <h2 className="text-3xl lg:text-4xl font-black text-navy mb-6 group-hover:text-blue-600 transition-colors">
                        {filteredPosts[0].title}
                      </h2>
                      <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                        {filteredPosts[0].excerpt}
                      </p>
                      <div className="flex items-center justify-between mt-auto">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                            {filteredPosts[0].author.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <p className="font-bold text-navy">{filteredPosts[0].author}</p>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-widest">{filteredPosts[0].role}</p>
                          </div>
                        </div>
                        <button className="flex items-center gap-2 font-black text-blue-600 hover:gap-3 transition-all">
                          Read Story <ArrowRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Posts Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
              {(activeCategory === 'all' && searchQuery === "" ? filteredPosts.slice(1) : filteredPosts).map((post, idx) => (
                <div key={post.id} className="group flex flex-col reveal stagger-1">
                  <div className="relative h-64 rounded-[32px] overflow-hidden mb-8 shadow-lg">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-4 py-1.5 bg-white/90 backdrop-blur-md rounded-full text-[10px] font-black text-navy uppercase tracking-widest shadow-sm">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mb-4 text-xs font-bold text-gray-400">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {post.date}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-navy mb-4 group-hover:text-blue-600 transition-colors leading-tight">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-8 line-clamp-3 leading-relaxed">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between mt-auto pt-6 border-t border-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500">
                        {post.author.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="text-sm font-bold text-navy">{post.author}</span>
                    </div>
                    <button className="text-blue-600 hover:text-navy transition-colors">
                      <ArrowRight className="w-6 h-6" />
                    </button>
                  </div>
                </div>
              ))}

              {filteredPosts.length === 0 && (
                <div className="col-span-full text-center py-32 bg-gray-50 rounded-[48px] border-2 border-dashed border-gray-200">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                    <Search className="w-10 h-10 text-gray-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy">No articles found</h3>
                  <p className="text-gray-500 mt-2">Try searching for a different topic or category.</p>
                  <button 
                    onClick={() => {setActiveCategory('all'); setSearchQuery("");}}
                    className="mt-8 text-blue-600 font-bold underline"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Newsletter / CTA */}
        <section className="shell-navy relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-sky rounded-full blur-[120px]" />
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center mx-auto mb-8 text-sky">
              <Mail className="w-10 h-10" />
            </div>
            <h2 className="text-white mb-8">Get the latest insights delivered.</h2>
            <p className="text-xl text-blue-100 mb-12">
              Subscribe to the PMS newsletter for bi-weekly engineering and product updates.
            </p>
            <form className="max-w-lg mx-auto flex flex-col sm:flex-row gap-4">
              <input
                required
                type="email"
                placeholder="Enter your work email"
                className="flex-1 px-6 py-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-blue-200 focus:outline-none focus:ring-2 focus:ring-sky transition-all"
              />
              <button type="submit" className="btn-secondary whitespace-nowrap">
                Subscribe
              </button>
            </form>
            <p className="text-sm text-blue-300 mt-6">No spam, just signal. Unsubscribe at any time.</p>
          </div>
        </section>
      </main>

      <MarketingFooter onNavClick={handleNavClick} />
    </div>
  );
};

export default BlogPage;
